// @ts-nocheck
import { useRef, useState, useEffect, memo } from "react";
import { VariableSizeList, areEqual } from "react-window";
import { getDocument } from "pdfjs-dist/webpack";
import classNames from "classnames";
import printJS from "print-js";
import lodash from "lodash";
import { IconButton } from "./IconButton";

type PdfProps = {
    data?: any;
    defaultPage?: number;
    thumbnail?: boolean;
    print?: boolean;
    download?: boolean;
};

export const Pdf = (props: PdfProps) => {
    const { data, defaultPage = 1, thumbnail, print, download } = props;

    const thumbnailWidth = 160;

    const _context = useRef({
        thumbnailWidth,
        filename: "",
        pageRendering: false,
        base64: null,
        pageWidth: null,
        canvas: null,
        numPages: null,
        pdfDoc: null,
        list: null,
        worker: null,
        blob: null,
        url: null,
        buffer: null,
        itemsHeight: [],
        pageContainer: null,
        page: defaultPage,
    });

    const [pdfDoc, setPdfDoc] = useState(null);
    const [page, setPage] = useState(defaultPage);

    useEffect(() => {
        _context.current.page = page;
    }, [page]);

    useEffect(() => {
        const handleResize = lodash.debounce(() => {
            _context.current.pageWidth = _context.current.pageContainer.clientWidth;
            renderPage(_context.current.page);
        }, 300);

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    useEffect(() => {
        if (data instanceof File) {
            if (data.type === "application/pdf") {
                _context.current.filename = data.name;

                const fileReader = new FileReader();
                fileReader.readAsArrayBuffer(data);
                fileReader.onload = () => {
                    const buffer = fileReader.result;
                    _context.current.buffer = buffer;

                    const pdf = getDocument(lodash.cloneDeep(_context.current.buffer));
                    pdf.promise.then(
                        (pdfDoc: any) => {
                            _context.current.pdfDoc = pdfDoc;
                            _context.current.numPages = pdfDoc.numPages;
                            _context.current.itemsHeight = [];
                            setPdfDoc(pdfDoc);
                            setPage(defaultPage);
                        },
                        (reason) => {
                            console.log(reason);
                        },
                    );
                };
            }
        }
    }, [data]);

    useEffect(() => {
        if (!data) return;
        _context.current.filename = data.filename;

        const buffer = lodash.cloneDeep(data.buffer);
        _context.current.buffer = buffer;

        const pdf = getDocument(lodash.cloneDeep(_context.current.buffer));
        pdf.promise.then(
            (pdfDoc: any) => {
                _context.current.pdfDoc = pdfDoc;
                _context.current.numPages = pdfDoc.numPages;
                _context.current.itemsHeight = [];
                setPdfDoc(pdfDoc);
                setPage(defaultPage);
            },
            (reason) => {
                console.log(reason);
            },
        );
    }, [data?.__t]);

    useEffect(() => {
        if (!pdfDoc) return;
        renderPage(page);
    }, [pdfDoc, page]);

    const renderPage = (num) => {
        if (!_context.current.pdfDoc) return;
        if (_context.current.pageRendering) return;
        _context.current.pageRendering = true;

        _context.current.pdfDoc.getPage(num).then((page) => {
            const canvas = _context.current.canvas;
            const viewport = page.getViewport({ scale: 1 });
            const { width, height } = viewport;
            const renderWidth = _context.current.pageWidth;

            let scale;
            if (height > width) scale = Math.max(renderWidth / width, renderWidth / height);
            else scale = Math.min(renderWidth / width, renderWidth / height);

            const renderHeight = (renderWidth * height) / width;

            canvas.width = renderWidth;
            canvas.height = renderHeight;

            const renderViewport = page.getViewport({ scale });

            const renderTask = page.render({
                canvasContext: canvas.getContext("2d"),
                viewport: renderViewport,
            });

            renderTask.promise.then(() => {
                _context.current.pageRendering = false;
            });
        });
    };

    const handleClickPrev = () => {
        if (_context.current.pageRendering) return;
        if (page === 1) return;
        setPage((prev) => --prev);
    };

    const handleClickNext = () => {
        if (_context.current.pageRendering) return;
        if (page === pdfDoc.numPages) return;
        setPage((prev) => ++prev);
    };

    const handleClickPrint = () => {
        if (!_context.current.base64) {
            let binary = "";
            const bytes = new Uint8Array(_context.current.buffer);
            for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
            const base64 = window.btoa(binary);
            _context.current.base64 = base64;
        }

        printJS({ printable: _context.current.base64, type: "pdf", base64: true });
    };

    const handleClickDownload = () => {
        if (!_context.current.url) {
            const blob = new Blob([_context.current.buffer]);
            const url = URL.createObjectURL(blob);
            _context.current.blob = blob;
            _context.current.url = url;
        }

        const a = document.createElement("a");
        a.download = _context.current.filename;
        a.href = _context.current.url;
        a.click();
        a.remove();
    };

    return (
        <div className="flex flex-col gap-4 w-full">
            <div className="flex w-full justify-between items-center">
                {pdfDoc && (
                    <div className="flex gap-1 items-center">
                        {download && <IconButton icon="download" size="sm" onClick={handleClickDownload} />}
                        {print && <IconButton icon="printer" size="sm" onClick={handleClickPrint} />}
                        <IconButton icon="left" size="sm" onClick={handleClickPrev} />
                        <span>{` ${page} / ${pdfDoc.numPages}`}</span>
                        <IconButton icon="right" size="sm" onClick={handleClickNext} />
                    </div>
                )}
            </div>
            <div className="flex w-full gap-4">
                {thumbnail && (
                    <div className="flex flex-col gap-4 items-center w-[166px]">
                        {pdfDoc && (
                            <VariableSizeList
                                ref={(ref) => {
                                    if (!ref) return;
                                    if (_context.current.list) return;
                                    _context.current.list = ref;
                                }}
                                itemCount={pdfDoc.numPages}
                                width={thumbnailWidth + 6}
                                height={800}
                                itemSize={(index) => _context.current.itemsHeight[index] || 0}
                                itemData={{ pdfContext: _context, pdfDoc, page, setPage }}
                            >
                                {Thumbnail}
                            </VariableSizeList>
                        )}
                    </div>
                )}
                <div
                    ref={(ref) => {
                        if (!ref) return;
                        _context.current.pageContainer = ref;
                        _context.current.pageWidth = ref.clientWidth;
                    }}
                    className="relative flex-1"
                    style={{ maxWidth: `calc(100% - ${thumbnail ? 182 : 0}px)` }}
                >
                    <canvas
                        ref={(ref) => {
                            if (!ref) return;
                            if (_context.current.canvas) return;
                            _context.current.canvas = ref;
                        }}
                        className="absolute left-0 top-0"
                    />
                </div>
            </div>
        </div>
    );
};

const Thumbnail = memo((props: any) => {
    const { data, index, style } = props;
    const { pdfDoc, pdfContext, page, setPage } = data;

    const _context = useRef({ canvas: null });

    useEffect(() => {
        const canvas = _context.current.canvas;
        if (!canvas) return;

        pdfDoc.getPage(index + 1).then((page) => {
            const viewport = page.getViewport({ scale: 1 });
            const { width, height } = viewport;
            const renderWidth = pdfContext.current.thumbnailWidth;
            const scale = Math.min(renderWidth / width, renderWidth / height);
            const renderHeight = (renderWidth * height) / width;
            canvas.width = renderWidth;
            canvas.height = renderHeight;
            pdfContext.current.itemsHeight[index] = renderHeight + 32;
            const renderViewport = page.getViewport({ scale });
            const renderContext = {
                canvasContext: canvas.getContext("2d"),
                viewport: renderViewport,
            };
            page.render(renderContext);
            pdfContext.current.list.resetAfterIndex(index);
        });
    }, [pdfDoc]);

    const handleClickThumbnail = () => {
        setPage(index + 1);
    };

    return (
        <div className="flex items-center" style={{ ...style }}>
            <div className={classNames("cursor-pointer bg-black")} onClick={handleClickThumbnail}>
                <canvas
                    ref={(ref) => {
                        if (!ref) return;
                        if (_context.current.canvas) return;
                        _context.current.canvas = ref;
                    }}
                    className={classNames(page === index + 1 && "opacity-80")}
                />
            </div>
        </div>
    );
}, areEqual);
